import React, { useRef, useState, useCallback ,useMemo, useEffect} from 'react';
import { Input, Tooltip, Button, Spin, Select, Flex } from 'antd';
import { DeleteOutlined, PlusOutlined, EditOutlined, EyeFilled } from '@ant-design/icons';
import './App.css';
import replaceSymbols from './utils/replaceSymbols';
import RefereshLogoSvg from '../../dmn/src/assets/images/RefereshLogo';

const ROW_HEIGHT = 76;
const { Option } = Select;

const MIN_COL_WIDTH = 160;
const getMinWidth = (_header: string) => MIN_COL_WIDTH;

interface Props {
  dmnData: any;
  setDmnData: (data: any) => void;
  headerSequence: string[];
  type: string;
  inputFields: any[];
  outputFields: any[];
  validationErrors?: { [key: string]: string };
  setSelectedRow: (index: number) => void;
  setSelectedCol: (index: number) => void;
  setValueType: (val: string) => void;
  setEditHeader: (header: string) => void;
  setEditValue: (val: string) => void;
  setFieldType: (type: string) => void;
  setShowModal: (val: boolean) => void;
  setHeaderSequence: (updater: (prev: string[]) => string[]) => void;
  setSelectedColumnIndex: (index: number) => void;
  setSelectedColumnFrom: (header: string) => void;
  setShowColumnModal: (val: boolean) => void;
  setSelectedColumnFromType: (type: string) => void;
  setSelectedColumn: (header: string) => void;
  setHeaderVal: (header: string) => void;
  setHeaderValFrom: (header: string) => void;
  setShowHeaderModal: (val: boolean) => void;
  handleReset: (e: any) => void,
  hasGridView?: boolean;
  hasGridEdit?: boolean;
  hasGridAddColumn?: boolean;
  hasGridAddRow?: boolean;
  hasShowGridFilter?: boolean;
  // showReset?: boolean;}
}

// 🔧 CellInput Component
const CellInput = ({
  rowIndex,
  header,
  initialValue,
  onCommit,
  disabled,
  isOutput,
  type
}: {
  rowIndex: number;
  header: string;
  initialValue: string;
  onCommit: (val: string) => void;
  disabled: boolean;
  isOutput: boolean;
  type: string
}) => {
  const [value, setValue] = useState(initialValue);
  const inputRef = useRef<any>(null);
  const hasChangedRef = useRef(false);

  // 🧠 Fix: Sync internal state with prop change
  React.useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isOutput && !disabled) {
      setValue(e.target.value);
      hasChangedRef.current = true;
    }
  };

  const handleBlur = () => {
    if (isOutput && hasChangedRef.current && value !== initialValue) {
      onCommit(value);
    }
    hasChangedRef.current = false;
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      inputRef.current?.blur();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setValue(initialValue);
      hasChangedRef.current = false;
      inputRef.current?.blur();
    }
  };
      const handleMouseEnter = () => {
    if (isOutput && !disabled) {
      inputRef.current?.focus();
    }
  };

  console.log(disabled, type, 'valuecheckehcke')

   if (isOutput) {
  return (
    <Input
      ref={inputRef}
      size="middle"
      value={replaceSymbols(value, isOutput, type)}
      disabled={disabled}
      readOnly={!isOutput} 
      onChange={handleChange}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      onMouseEnter={handleMouseEnter}
      className={!isOutput ? 'dmn-cell-input-readonly' : 'dmn-cell-input-editable'}
      style={{ cursor: disabled ? 'not-allowed' : (isOutput ? 'text' : 'default') }}

    />
  );
}
return (
    <div
      className="dmn-cell-input-readonly"
      style={{ cursor: disabled ? 'not-allowed' : 'default' }}
    >
      {replaceSymbols(value, isOutput, type)}
    </div>
  );
};


const VirtualizedDMNTable: React.FC<Props> = ({
  dmnData,
  setDmnData,
  headerSequence,
  type,
  inputFields,
  outputFields,
  validationErrors = {},
  setSelectedRow,
  setSelectedCol,
  setValueType,
  setEditHeader,
  setEditValue,
  setFieldType,
  setShowModal,
  setHeaderSequence,
  setSelectedColumnIndex,
  setSelectedColumnFrom,
  setShowColumnModal,
  setSelectedColumnFromType,
  setSelectedColumn,
  setHeaderVal,
  setHeaderValFrom,
  setShowHeaderModal,
  handleReset,
  hasGridView = true,
  hasGridEdit = true,
  hasGridAddColumn = true,
  hasGridAddRow = true,
  hasShowGridFilter = true,
}) => {
  const [loading, setLoading] = useState(false);
  const [filteredInfo, setFilteredInfo] = useState<{ [key: string]: string[] }>({});
  const [sortedInfo, setSortedInfo] = useState<any>({});
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);      
const dragStateRef = useRef<{ header: string; startX: number; startWidth: number } | null>(null);
const colWidthsRef = useRef<Record<string, number>>({});
const lockedColsRef = useRef<Set<string>>(new Set());
const [hasResizedColumn, setHasResizedColumn] = useState(false);

  const applyFilters = useCallback(() => {
    if (!Object.keys(filteredInfo).length) return dmnData.data;
    return dmnData.data.filter((row: any) => {
      return Object.entries(filteredInfo).every(([key, filters]) => {
        if (!filters?.length) return true;
        const value = row[key]?.toString().trim();
        return filters.includes(value);
      });
    });
  }, [filteredInfo, dmnData]);

  const applySorting = useCallback(
    (data: any[]) => {
      if (!sortedInfo?.columnKey || !sortedInfo.order) return data;
      return [...data].sort((a, b) => {
        const aVal = a[sortedInfo.columnKey]?.toString() || '';
        const bVal = b[sortedInfo.columnKey]?.toString() || '';
        if (sortedInfo.order === 'ascend') return aVal.localeCompare(bVal);
        if (sortedInfo.order === 'descend') return bVal.localeCompare(aVal);
        return 0;
      });
    },
    [sortedInfo]
  );

  const filteredData = applySorting(applyFilters());

  const handleCellChange = (rowIndex: number, key: string, value: string) => {
    setDmnData((prev: any) => {
      const newData = [...prev.data];
      newData[rowIndex] = { ...newData[rowIndex], [key]: value };
      return { ...prev, data: newData };
    });
  };

  const handleDeleteRow = useCallback((rowIndex: number) => {
    setDmnData((prev: any) => {
      const newData = [...prev.data];
      newData.splice(rowIndex, 1);
      return { ...prev, data: newData };
    });
  }, [setDmnData]);

  const handleAddRow = useCallback((rowIndex: number) => {
    if (!headerSequence.length) return;
    const newRow = headerSequence.reduce((acc, header) => {
      acc[header] = '';
      return acc;
    }, {} as Record<string, string>);
    setDmnData((prev: any) => {
      const newData = [...prev.data];
      newData.splice(rowIndex + 1, 0, newRow);
      return { ...prev, data: newData };
    });
  }, [setDmnData, headerSequence]);

  const handleModalOnClick = useCallback((header: string) => {
    if ((type === 'Edit' || type === 'Create') && hasGridEdit) {
      setSelectedColumn(header);
      const inputField = dmnData?.inputFields?.find((item: any) => item.name === header);
      const outputField = dmnData?.outputFields?.find((item: any) => item.name === header);
      if (inputField) {
        setSelectedColumnFromType(inputField.type);
      } else if (outputField) {
        setSelectedColumnFromType(outputField.type);
      } else {
        setSelectedColumnFromType('');
      }
      setShowHeaderModal(true);
      setHeaderVal(header);
      setHeaderValFrom(header);
    }
  }, [type, hasGridEdit, setSelectedColumn, dmnData, setSelectedColumnFromType, setShowHeaderModal, setHeaderVal, setHeaderValFrom]);

  const handleDeleteColumn = useCallback((index: number, headerName: string) => {
    const updatedData = dmnData.data.map((row: any) => {
      const newRow = { ...row };
      delete newRow[headerName];
      return newRow;
    });
    const updatedInputFields = inputFields.filter((field) => field.name !== headerName);
    const updatedOutputFields = outputFields.filter((field) => field.name !== headerName);
    setDmnData({
      ...dmnData,
      inputFields: updatedInputFields,
      outputFields: updatedOutputFields,
      data: updatedData,
    });
    setHeaderSequence((prev) => prev.filter((h) => h !== headerName));
  }, [dmnData, inputFields, outputFields, setDmnData, setHeaderSequence]);

  const getUniqueValues = (data: any[], key: string): string[] => {
    const values = new Set<string>();
    data.forEach((item) => {
      const val = item[key];
      if (val && typeof val === 'string') {
        val.split(',').forEach((v) => {
          const trimmed = v.trim();
          if (trimmed && trimmed.toLowerCase() !== 'null') {
            values.add(trimmed);
          }
        });
      }
    });
    return Array.from(values);
  };

    const groupRuns = useMemo(() => {
    const runs: { group: 'input' | 'output'; cols: string[] }[] = [];
    headerSequence.forEach((header) => {
      const isOutput = outputFields?.some((field) => field.name === header);
      console.log(outputFields,isOutput,"outputFieldsoutputFields")
      const group = isOutput ? 'output' : 'input';
      const last = runs[runs.length - 1];
      if (last && last.group === group) {
        last.cols.push(header);
      } else {
        runs.push({ group, cols: [header] });
      }
    });
    return runs;
  }, [headerSequence, outputFields]);
  // console.log(outputFields,isOutput,"outputFieldsoutputFields")
  const applyGroupWidths = useCallback(() => {
  if (!containerRef.current) return;
  groupRuns.forEach((run, idx) => {
    const total = run.cols.reduce(
      (sum, h) => sum + (colWidthsRef.current[h] || getMinWidth(h)),
      0
    );
    const groupEl = containerRef.current!.querySelector(`[data-dmn-group="${idx}"]`) as HTMLElement | null;
    groupEl?.style.setProperty('--dmn-group-width', `${total}px`);
  });
}, [groupRuns]);

const applyColumnWidth = useCallback((header: string, widthPx: number) => {
  colWidthsRef.current[header] = widthPx;
  const escaped = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(header) : header;
  containerRef.current
    ?.querySelectorAll(`[data-dmn-col="${escaped}"]`)
    .forEach((el) => (el as HTMLElement).style.setProperty('--dmn-col-width', `${widthPx}px`));
}, []);

const recalcFit = useCallback((extraLockedHeader?: string) => {
  if (!containerRef.current || headerSequence.length === 0) return;
  const reserved = type !== 'Show' ? 96 : 0;
  const containerWidth = measureRef.current.clientWidth - reserved;
  if (!containerWidth) return;

  const isLocked = (h: string) => lockedColsRef.current.has(h) || h === extraLockedHeader;

  const lockedTotal = headerSequence.reduce(
    (sum, h) => (isLocked(h) ? sum + (colWidthsRef.current[h] || getMinWidth(h)) : sum),
    0
  );
  const unlocked = headerSequence.filter((h) => !isLocked(h));
  if (unlocked.length === 0) {
    applyGroupWidths();
    return;
  }

  const remaining = Math.max(containerWidth - lockedTotal, unlocked.length * MIN_COL_WIDTH);
  const share = Math.floor(remaining / unlocked.length);
  let allocated = 0;
  unlocked.forEach((h, i) => {
    const isLast = i === unlocked.length - 1;
    const w = Math.max(isLast ? remaining - allocated : share, getMinWidth(h));
    applyColumnWidth(h, w);
    allocated += w;
  });
  applyGroupWidths();
}, [headerSequence, type, applyColumnWidth, applyGroupWidths]);

useEffect(() => {
  recalcFit();

  let rafId: number | null = null;
  const ro = new ResizeObserver(() => {
    if (rafId !== null) cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(() => {
      recalcFit();
    });
  });

  if (measureRef.current) ro.observe(measureRef.current);   
  return () => {
    if (rafId !== null) cancelAnimationFrame(rafId);
    ro.disconnect();
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [recalcFit]);

const startResize = (header: string, e: React.MouseEvent<HTMLDivElement>) => {
  e.preventDefault();
  e.stopPropagation();
  const startX = e.clientX;
  const startWidth = colWidthsRef.current[header] || getMinWidth(header);
  dragStateRef.current = { header, startX, startWidth };
  const handle = e.currentTarget;
  handle.classList.add('is-dragging');

const onMove = (ev: MouseEvent) => {
  if (!dragStateRef.current) return;
  const { header, startX, startWidth } = dragStateRef.current;
  const newWidth = Math.max(getMinWidth(header), startWidth + (ev.clientX - startX));
  applyColumnWidth(header, newWidth);
  recalcFit(header); 
};
  
const onUp = () => {
  if (dragStateRef.current) {
    lockedColsRef.current.add(dragStateRef.current.header);
    setHasResizedColumn(true);
  }
  handle.classList.remove('is-dragging');
  dragStateRef.current = null;
  window.removeEventListener('mousemove', onMove);
  window.removeEventListener('mouseup', onUp);
  recalcFit();
};
  window.addEventListener('mousemove', onMove);
  window.addEventListener('mouseup', onUp);
};

const resetColumnWidths = () => {
  lockedColsRef.current = new Set();
  colWidthsRef.current = {};
  setHasResizedColumn(false);
  recalcFit();
};
useEffect(() => {
  headerSequence.forEach((h) => {
    applyColumnWidth(h, colWidthsRef.current[h] || getMinWidth(h));
  });
  applyGroupWidths();
}, [dmnData?.data?.length, headerSequence, applyColumnWidth, applyGroupWidths]);

  if (loading) return <Spin />;
  if (!hasGridView) return null;

  return (
    <>
     {dmnData?.data?.length > 0 && type !== 'Show' ? (
        <div className="form-submit-btn-container !justify-end mb-5">
           <button onClick={(e) => handleReset(e)} className="reset-btn" >
            Reset
          </button>
          <button onClick={resetColumnWidths} className="reset-btn" type="button" disabled={!hasResizedColumn}>
            <Flex className='gap-sm items-center' >
              <RefereshLogoSvg/>
              Reset Widths
            </Flex>
            
          </button>

        </div>
      ) : null}
    <div  className="dmn-table-measure"> 
    <div ref={containerRef} className="virtual-table dmn-table dmn-table-bordered">
    <div ref={measureRef} className="dmn-table-scroll ">

<div className="dmn-table-group-pane dmn-body-row-divider">

 {groupRuns.map((run, idx) => (
  <div
    key={`${run.group}-${idx}`}
    data-dmn-group={idx}
    className={`dmn-group ${run.group === 'output' ? 'dmn-group-output' : 'dmn-group-input'}`}
  >
    <span className="dmn-group-label">
      {run.group === 'output' ? 'Output' : 'Input Conditions'}
    </span>
  </div>
))}

{type !== 'Show' && (
  <div className=" dmn-actions-col !border-r-0 flex-shrink-0 dmn-group-actions-spacer "></div>
)}
</div>
<div className="flex  font-semibold dmn-table-head-pane dmn-body-row-divider">
  <span className="text-center"></span>
  {headerSequence.map((header, index) => {
    const isInput = inputFields?.some((field) => field.name === header);
    const isOutput = outputFields?.some((field) => field.name === header);
    const inputFieldType = inputFields?.find((item) => item.name === header)?.type;
    const outputFieldType = outputFields?.find((item) => item.name === header)?.type;
    const colType = isOutput ? outputFieldType : inputFieldType;
    return (
      <div
        key={header}
         data-dmn-col={header}
        className={`p-2  min-w-0 flex flex-col overflow-hidden dmn-col ${isOutput ? 'dmn-col-output-header' : ''}`}
      >
        {/* label row */}
        <div className="flex items-center mb-sm justify-between w-full">
          <span
            className="flex-1 pr-2 truncate cursor-pointer whitespace-normal break-words leading-tight"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handleModalOnClick(header);
            }}
          >
            {header}
          </span>
          <div className='flex gap-2 '>
            {(type === 'Edit' || type === 'Create') && hasGridAddColumn && (
              <div
                className="relative cursor-pointer text-gray-600"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setSelectedColumnIndex(index);
                  setSelectedColumnFrom(header);
                  setShowColumnModal(true);
                }}
              >
                <PlusOutlined />
              </div>
            )}
            {(type === 'Edit' || type === 'Create') && hasGridEdit &&
              ((isInput && dmnData?.inputFields?.length > 1) ||
                (isOutput && dmnData?.outputFields?.length > 1)) && (
                <div
                  className="relative cursor-pointer text-gray-600"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleDeleteColumn(index, header);
                  }}
                >
                  <DeleteOutlined />
                </div>
              )}
          </div>
        </div>
        {hasShowGridFilter ? (
        <Select
          className="text-md bg-white rounded-sm h-8 w-full dmn-column-filter !bg-neutral-100 mt-1.5 "
          size="middle"
          showSearch
          value={filteredInfo[header]?.[0] || ''}
          onChange={(value) => {
            setFilteredInfo((prev) => ({
              ...prev,
              [header]: value ? [value] : [],
            }));
          }}
          placeholder="All"
          filterOption={(input, option) => {
            if (!option) return false;
            const displayValue = replaceSymbols(String(option.value), isOutput, colType);
            return displayValue.toLowerCase().includes(input.toLowerCase());
          }}
        >
          <Option value="">All</Option>
          {getUniqueValues(dmnData.data, header).map((opt) => (
            <Option key={opt} value={opt}>
              {replaceSymbols(opt, isOutput, colType)}
            </Option>
          ))}
        </Select>
  ):null}
        <div className="dmn-col-resize-handle" onMouseDown={(e) => startResize(header, e)} />
      </div>
   
    );
  })}
  {type !== 'Show' && <div className=" dmn-actions-col !border-r-0 flex-shrink-0 h-auto items-center p-2 ">Actions</div>}
</div>

      <div  className="dmn-body-rows-wrapper" >
        {filteredData.map((row, index) => (
          <div
            key={index}
            className={`flex dmn-body-row-divider ${index % 2 ? ' bg-gray-50' : 'bg-white'}`}
            style={{ minHeight: ROW_HEIGHT }}
          >
            {headerSequence.map((header, colIndex) => {
              const inputField = inputFields?.find((item) => item.name === header);
              const outputField = outputFields?.find((item) => item.name === header);
              const isOutput = Boolean(outputField);
              const text = row[header] ?? '';
              const inputFieldType = inputFields?.find((item) => item.name === header)?.type
              const outputFieldType = outputFields?.find((item) => item.name === header)?.type;

              let feildType
              if (isOutput) {
                feildType = outputFieldType
              } else {
                feildType = inputFieldType
              }
              return (
                <div key={header} data-dmn-col={header} className="p-2  min-w-0 dmn-body-cell-divider dmn-col ">
                   <div className='flex h-full items-center'>
                    <span title={replaceSymbols(text, isOutput, feildType)} className="w-full">
                      <CellInput
                        rowIndex={index}
                        header={header}
                        initialValue={text}
                        onCommit={(val) => handleCellChange(index, header, val)}
                        disabled={type === 'Show'|| !hasGridEdit}
                        isOutput={isOutput}
                        type={feildType}

                      />
                    </span>
                    {!isOutput && type !== 'Show' && hasGridEdit &&  (
                      <div
                        className="cursor-pointer mt-1  dmn-row-hover-actions "
                        onClick={() => {
                          setSelectedRow(index);
                          setSelectedCol(colIndex);
                          const dataType = inputField?.type;
                          setValueType(dataType);
                          setEditHeader(header);
                          setEditValue(
                            text?.includes('not')
                              ? text.replace(/not/g, '').replace(/"/g, '').trim().split(/\s+/).join(', ')
                              : text.replace(/"/g, '') || ''
                          );
                          setFieldType(
                            text === 'null' || text?.trim() === ''
                              ? 'null'
                              : dataType === 'double'
                                ? text.includes('to')
                                  ? 'range'
                                  : 'comparison'
                                : text.includes('not')
                                  ? 'matchesNone'
                                  : 'matches'
                          );
                          setShowModal(true);
                        }}
                      >
                        {type === 'Show' ? <EyeFilled className='ml-2' /> : <EditOutlined className='ml-2' />}
                      </div>
                    )}
                  </div>
                  {validationErrors[`${index}-${colIndex}`] && (
                    <div style={{ color: 'red' }}>{validationErrors[`${index}-${colIndex}`]}</div>
                  )}
                </div>
              );
            })}
            {type !== 'Show' && (
               <div className="flex dmn-actions-col items-center !border-r-0 gap-2 flex-shrink-0 p-2">
                <Button className='border border-1 border-solid border-red-500 text-red-500 rounded-md' style={{ paddingLeft: '8px', paddingRight: '8px', width: 'auto' }} size="middle" icon={<DeleteOutlined />} onClick={() => handleDeleteRow(index)} disabled={!hasGridAddRow} />
                <Button className='border border-1 border-solid border-primary text-primary rounded-md' style={{ paddingLeft: '8px', paddingRight: '8px', width: 'auto' }} size="middle" icon={<PlusOutlined />} onClick={() => handleAddRow(index)} disabled={!hasGridAddRow} />
              </div>
            )}
          </div>
        ))}
      </div>
   
    </div>
    </div>
    </div>
    </>
  );
};

export default VirtualizedDMNTable;
